import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLanguage } from './LanguageContext.jsx';
import { translateToEnglish, translateToEnglishCachedSync } from '../utils/translate.js';
import { translateStaticText } from '../i18n/staticText.js';

const TranslationContext = createContext(null);

export const TranslationProvider = ({ children }) => {
  const { isEnglish, lang } = useLanguage();

  const translationEnabled = isEnglish;

  const tStatic = useCallback(
    (text) => translateStaticText(text, lang),
    [lang]
  );

  // Event-only API translation. Static UI text should use `tStatic`.
  const tEvent = useCallback(
    (text) => {
      const src = typeof text === 'string' ? text : '';
      if (!translationEnabled) return src;

      const cached = translateToEnglishCachedSync(src);
      if (cached) return cached;

      // Return a Promise when not cached (callers can decide how to render).
      return translateToEnglish(src);
    },
    [translationEnabled]
  );

  const tEventSync = useCallback(
    (text) => {
      const src = typeof text === 'string' ? text : '';
      if (!translationEnabled) return src;
      return translateToEnglishCachedSync(src) || src;
    },
    [translationEnabled]
  );

  const value = useMemo(
    () => ({
      lang,
      isEnglish,
      translationEnabled,
      tStatic,
      tEvent,
      tEventSync,
    }),
    [lang, isEnglish, translationEnabled, tStatic, tEvent, tEventSync]
  );

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};

export const useTranslation = () => {
  const ctx = useContext(TranslationContext);
  if (!ctx) throw new Error('useTranslation must be used within a TranslationProvider');
  return ctx;
};
